import { useSelector } from 'react-redux';

import { Text, TextType } from 'ui-components/components/text';

import { messages } from './messages';
import Global from '../../../store/global-selectors';
import { OrdersGridColumns } from '../../../store/orders/grid/orders-grid-columns';
import { PickBatchExceptionType } from '../../../enums/pick-batch-exception-type';
import type { MessageDescriptor } from 'react-intl';

const enumToHumanReadableStringMap: Record<PickBatchExceptionType, MessageDescriptor> = {
  [PickBatchExceptionType.None]: messages.pickingExceptionNone,
  [PickBatchExceptionType.NotEnoughStock]: messages.pickingExceptionNotEnoughStock,
  [PickBatchExceptionType.ProductDamaged]: messages.pickingExceptionProductDamaged,
  [PickBatchExceptionType.ProductNotFound]: messages.pickingExceptionProductNotFound,
  [PickBatchExceptionType.Other]: messages.pickingExceptionOther,
};

type PickingExceptionColumnProps = {
  fulfillmentPlanId: string;
};

const PickingExceptionColumn = ({ fulfillmentPlanId }: PickingExceptionColumnProps) => {
  const exceptionType = useSelector((state) =>
    Global.selectEntitiesFulfillmentPlanPickBatchExceptionType(state, { fulfillmentPlanId }),
  ) as PickBatchExceptionType;

  const pickBatchExceptionInfo = useSelector((state) =>
    Global.selectEntitiesFulfillmentPlanProperty(state, {
      fulfillmentPlanId,
      property: 'pickBatchExceptionInfo',
    }),
  );

  if (exceptionType === PickBatchExceptionType.None) {
    return null;
  }

  const productExceptions = pickBatchExceptionInfo?.productExceptions;
  const hasMultipleExceptions = productExceptions && Object.keys(productExceptions).length > 1;

  if (hasMultipleExceptions) {
    return <Text textType={TextType.Paragraph} value={messages.pickingExceptionMultiple} />;
  }

  const message = enumToHumanReadableStringMap[exceptionType] ?? messages.pickingExceptionOther;

  return <Text textType={TextType.Paragraph} value={message} />;
};

type RenderOptions = {
  isExpanded?: boolean;
  isFirstRow?: boolean;
};

type ColumnData = {
  id: string;
};

export const pickingExceptionColumn = {
  getMessageDescriptor: () => messages.pickingException,
  id: OrdersGridColumns.PickingException,
  render: (data: ColumnData | null, { isExpanded = false, isFirstRow = true }: RenderOptions = {}) => {
    if (isExpanded && !isFirstRow) {
      return null;
    }
    if (!data) {
      return null;
    }

    return <PickingExceptionColumn fulfillmentPlanId={data.id} />;
  },
};
